import { memo, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { ReportGranularity, ReportPeriodDTO } from '@six-pockets/shared';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import { compactINR, niceTicks, periodLabel } from '../utils';
import styles from '@/styles/components/RevenueTrendChart/style.scss';

export type TrendSeries = 'total' | 'bookings' | 'condiments';

interface RevenueTrendChartProps {
  periods: ReportPeriodDTO[];
  granularity: ReportGranularity;
  series: TrendSeries;
}

const HEIGHT = 240;
const M = { top: 16, right: 8, bottom: 28, left: 52 };
const MAX_BAR = 24;
const GAP = 2; // surface gap between stacked segments
const LABEL_SPACING = 64; // the least room an x label needs

/** A column with a 4px rounded top, square at the baseline. */
function columnPath(x: number, y: number, w: number, h: number, round: boolean): string {
  if (h <= 0) return '';
  const r = round ? Math.min(4, w / 2, h) : 0;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

/**
 * Revenue per day (or month) as columns: bookings and condiments stacked for Total, or one of them on its own.
 * Hover, or focus and the arrow keys, shows a period's figures. The breakdown table holds the same numbers.
 */
export const RevenueTrendChart = memo(function RevenueTrendChart({ periods, granularity, series }: RevenueTrendChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.round(entry!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = periods.length;
  const parts = (p: ReportPeriodDTO) =>
    series === 'total' ? [p.bookingsRevenue, p.condimentsRevenue] : series === 'bookings' ? [p.bookingsRevenue, 0] : [0, p.condimentsRevenue];
  const ticks = niceTicks(Math.max(0, ...periods.map((p) => parts(p)[0]! + parts(p)[1]!)));
  const yMax = ticks[ticks.length - 1]!;

  const plotW = width - M.left - M.right;
  const plotH = HEIGHT - M.top - M.bottom;
  const slot = plotW / Math.max(1, n);
  const barW = Math.max(1, Math.min(MAX_BAR, slot * 0.6));
  const y = (v: number) => M.top + plotH - (v / yMax) * plotH;
  const labelEvery = Math.max(1, Math.ceil(LABEL_SPACING / slot));

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const from = active ?? n - 1;
    setActive(Math.min(n - 1, Math.max(0, from + (e.key === 'ArrowRight' ? 1 : -1))));
  };

  const shown = active !== null ? periods[active] : null;
  const tipLeft = active !== null ? M.left + slot * (active + 0.5) : 0;

  return (
    <div className={styles.root}>
      {series === 'total' && (
        <ul className={styles.legend}>
          <li>
            <span className={cx(styles.swatch, styles.bookings)} /> Bookings
          </li>
          <li>
            <span className={cx(styles.swatch, styles.condiments)} /> Condiments
          </li>
        </ul>
      )}

      <div
        ref={wrapRef}
        className={styles.plot}
        tabIndex={0}
        role="img"
        aria-label={`Revenue ${series === 'total' ? '' : `from ${series} `}per ${granularity}. Use the arrow keys for each ${granularity}.`}
        onKeyDown={onKeyDown}
        onFocus={() => setActive((a) => a ?? n - 1)}
        onBlur={() => setActive(null)}
        onPointerLeave={() => setActive(null)}
      >
        <svg width={width} height={HEIGHT} className={styles.svg} aria-hidden>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} className={t === 0 ? styles.baseline : styles.grid} />
              <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className={styles.tick}>
                {compactINR(t)}
              </text>
            </g>
          ))}

          {periods.map((p, i) => {
            const [b, c] = parts(p) as [number, number];
            const x = M.left + slot * i + (slot - barW) / 2;
            const hB = (b / yMax) * plotH;
            const hC = (c / yMax) * plotH;
            // The gap separates the two segments only when both are there and it leaves something to see.
            const gap = b > 0 && c > 0 && hC > GAP + 1 ? GAP : 0;
            const dim = active !== null && active !== i && styles.dim;
            return (
              <g key={p.key}>
                {i === active && <rect x={M.left + slot * i} y={M.top} width={slot} height={plotH} className={styles.band} />}
                <path d={columnPath(x, y(b), barW, hB, c === 0)} className={cx(styles.bookings, dim)} />
                <path d={columnPath(x, y(b) - hC, barW, hC - gap, true)} className={cx(styles.condiments, dim)} />
                {(n - 1 - i) % labelEvery === 0 && (
                  <text x={M.left + slot * (i + 0.5)} y={HEIGHT - 8} textAnchor="middle" className={styles.tick}>
                    {periodLabel(granularity, p.key)}
                  </text>
                )}
                {/* The hit target is the whole slot, not the painted column. */}
                <rect
                  x={M.left + slot * i}
                  y={M.top}
                  width={slot}
                  height={plotH}
                  className={styles.hit}
                  onPointerEnter={() => setActive(i)}
                  onPointerMove={() => setActive(i)}
                />
              </g>
            );
          })}
        </svg>

        {shown && (
          <div className={cx(styles.tip, tipLeft > width / 2 && styles.tipLeft)} style={{ left: tipLeft }} role="status">
            <span className={styles.tipTitle}>{periodLabel(granularity, shown.key)}</span>
            {series !== 'condiments' && (
              <span className={styles.tipRow}>
                <span className={cx(styles.key, styles.bookings)} />
                <b>{formatINR(shown.bookingsRevenue)}</b> Bookings
              </span>
            )}
            {series !== 'bookings' && (
              <span className={styles.tipRow}>
                <span className={cx(styles.key, styles.condiments)} />
                <b>{formatINR(shown.condimentsRevenue)}</b> Condiments
              </span>
            )}
            {series === 'total' && (
              <span className={cx(styles.tipRow, styles.tipTotal)}>
                <b>{formatINR(shown.revenue)}</b> Total
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
